import React, { useState, useEffect } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../styles/EntityManagement.css';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Divider } from
'@mui/material';
import EntityTable from '../components/EntityManagement/EntityTable';
import api from '../services/api';
import AssignUserDialog from '../components/AssignUserDialog';
import SearchableDataTable from '../components/SearchableDataTable';
import { usePointInTimeFilter } from '../hooks/usePointInTimeFilter';
import EntityDialog from '../components/EntityManagement/EntityDialog';
import { COUNTRIES, TAX_TYPES, TAX_SUB_TYPES, LEGAL_TYPES } from '../constants/dropdownOptions';

const EntityManagement = () => {
  // Use shared constants for consistency between UI and validation
  const jurisdictions = COUNTRIES;
  const legalTypes = LEGAL_TYPES;
  const taxTypes = TAX_TYPES;
  const taxSubTypes = TAX_SUB_TYPES;

  // State for entities and organizations
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  // Get PointInTime filter from context
  const { selectedPointInTime, availablePointInTimes } = usePointInTimeFilter();

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEntity, setCurrentEntity] = useState({
    code: '',
    name: '',
    taxId: '',
    region: '',
    country: '',
    currency: '',
    legalType: '',
    taxType: '',
    taxSubType: '',
    active: true,
    isTopSide: false,
    isTopSideUS: false,
    isBranch: false,
    entityShortName: '',
    segment: '',
    reportingBasis: '',
    taxYearEnd: '',
    taxFilingEntityCode: '',
    ownership: '',
    countryOfInc: '',
    countryASU: '',
    countryCbCR: '',
    countryOECD: '',
    PointInTime: ''
  });


  // State for user management
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedEntityForUsers, setSelectedEntityForUsers] = useState(null);
  const [assignUserDialogOpen, setAssignUserDialogOpen] = useState(false);
  const [entityUsers, setEntityUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Point In Time management state
  const [pointInTimes, setPointInTimes] = useState([]);
  const [loadingPointInTimes, setLoadingPointInTimes] = useState(false);

  // State for row selection
  const [selectedRows, setSelectedRows] = useState([]);

  // Fetch entities from API
  const fetchEntities = async () => {
    setLoading(true);
    try {
      const [entitiesResponse, pointInTimesResponse] = await Promise.all([
      api.get('/entity-models/master'),
      api.get('/point-in-times')]
      );

      // Create lookup map for PointInTime codes to names
      const pointInTimeMap = new Map();
      (pointInTimesResponse.data.data || []).forEach((pit) => {
        pointInTimeMap.set(pit.Code, pit.Name);
      });

      // Transform API data to match frontend schema
      const transformedEntities = (entitiesResponse.data.data || []).map((entity) => ({
        id: entity.id,
        code: entity.EntityCode,
        name: entity.EntityName,
        region: entity.Region,
        country: entity.Country,
        currency: entity.Currency,
        legalType: entity.LegalType,
        taxType: entity.TaxType,
        taxSubType: entity.TaxSubType,
        active: entity.Active,
        isTopSide: entity.IsTopSide,
        isTopSideUS: entity.IsTopSideUS,
        isBranch: entity.IsBranch,
        entityShortName: entity.EntityShortName,
        segment: entity.Segment,
        reportingBasis: entity.reportingBasis,
        taxYearEnd: entity.TaxYearEnd,
        taxFilingEntityCode: entity.taxFilingEntityCode,
        taxId: entity.TaxId,
        ownership: entity.Ownership,
        countryOfInc: entity.CountryOfInc,
        countryASU: entity.CountryASU,
        countryCbCR: entity.CountryCbCR,
        countryOECD: entity.CountryOECD,
        PointInTime: pointInTimeMap.get(entity.PointInTime) || entity.PointInTime || '',
        PointInTimeCode: entity.PointInTime || '' // Keep the original code for filtering
      }));

      setEntities(transformedEntities);
    } catch (error) {
      console.error('Error fetching entities:', error);
      setNotification({
        open: true,
        message: 'Error fetching entities',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Point In Times for the dropdown
  const fetchPointInTimes = async () => {
    setLoadingPointInTimes(true);
    try {
      const response = await api.get('/point-in-times');
      setPointInTimes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching point in times:', error);
    } finally {
      setLoadingPointInTimes(false);
    }
  };


  // Load data on component mount
  useEffect(() => {
    fetchEntities();
  }, []);

  // Handle adding a new entity
  const handleAdd = () => {
    setEditMode(false);
    // Use selected point in time from filter as default if it's not "All"
    const defaultPointInTime = selectedPointInTime && selectedPointInTime !== 'All' ?
    selectedPointInTime :
    '';
    setCurrentEntity({
      code: '',
      name: '',
      taxId: '',
      region: '',
      country: '',
      currency: '',
      legalType: '',
      taxType: '',
      taxSubType: '',
      active: true,
      isTopSide: false,
      isTopSideUS: false,
      isBranch: false,
      entityShortName: '',
      segment: '',
      reportingBasis: '',
      taxYearEnd: '',
      taxFilingEntityCode: '',
      ownership: '',
      countryOfInc: '',
      countryASU: '',
      countryCbCR: '',
      countryOECD: '',
      PointInTime: defaultPointInTime
    });
    fetchPointInTimes();
    setOpenDialog(true);
  };

  // Handle editing an entity
  const handleEdit = (entity) => {
    setEditMode(true);
    // Convert the entity data back to use PointInTime code for the dropdown
    const editEntity = {
      ...entity,
      PointInTime: entity.PointInTimeCode || '' // Use the code for the dropdown
    };
    setCurrentEntity(editEntity);
    fetchPointInTimes();
    setOpenDialog(true);
  };

  // Handle selection change
  const handleSelectionChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  // Handle deleting selected entities
  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected entities?`)) {
      try {
        // Create an array of promises for each delete operation
        const deletePromises = selectedRows.map((id) =>
        api.delete(`/entity-models/master/${id}`)
        );

        // Execute all delete operations and wait for them to complete
        Promise.all(deletePromises).
        then(() => {
          // Refresh the entities list from the API
          fetchEntities();
          // Clear selection
          setSelectedRows([]);
          // Show success notification
          setNotification({
            open: true,
            message: `Successfully deleted ${selectedRows.length} entities`,
            severity: 'success'
          });
        }).
        catch((error) => {
          console.error('Error deleting entities:', error);
          setNotification({
            open: true,
            message: 'Failed to delete entities',
            severity: 'error'
          });
        });
      } catch (error) {
        console.error('Error deleting entities:', error);
        setNotification({
          open: true,
          message: 'Failed to delete entities',
          severity: 'error'
        });
      }
    }
  };

  // Handle deleting a single entity
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this entity?')) {
      try {
        await api.delete(`/entity-models/master/${id}`);
        // Refresh the entities list from the API
        fetchEntities();
        setNotification({
          open: true,
          message: 'Entity deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting entity:', error);
        setNotification({
          open: true,
          message: 'Failed to delete entity',
          severity: 'error'
        });
      }
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing the dialog
  const handleClose = () => {
    setOpenDialog(false);
    setConfirmCloseOpen(false);
  };

  // Mapping of field names to display names (only required fields)
  const fieldDisplayNameMap = {
    code: 'Entity Code',
    name: 'Entity Name',
    region: 'Region',
    country: 'Country',
    currency: 'Currency',
    PointInTime: 'Point In Time'
    // taxType and taxSubType are optional, not validated here
  };

  // Validate required fields
  const validateEntity = (entity) => {
    const requiredFields = Object.keys(fieldDisplayNameMap);

    const missingFields = requiredFields.filter((field) => {
      const value = entity[field];
      const isEmpty = !value || typeof value === 'string' && value.trim() === '';
      return isEmpty;
    });

    if (missingFields.length > 0) {
      const missingFieldNames = missingFields.map((field) => fieldDisplayNameMap[field]);
      setNotification({
        open: true,
        message: `Please fill in the following required fields: ${missingFieldNames.join(', ')}`,
        severity: 'error'
      });
      return false;
    }
    return true;
  };

  // Handle saving an entity
  const handleSave = async (entityData) => {
    // Use the entity data passed from the dialog, not the parent state
    const entityToValidate = entityData || currentEntity;

    if (!validateEntity(entityToValidate)) {
      return;
    }
    try {
      // Use the validated entity data
      const entityToSave = entityToValidate;

      // Transform frontend entity to match backend schema
      const backendEntity = {
        EntityCode: entityToSave.code,
        EntityName: entityToSave.name,
        Region: entityToSave.region,
        Country: entityToSave.country,
        Currency: entityToSave.currency,
        TaxType: entityToSave.taxType,
        TaxSubType: entityToSave.taxSubType,
        Active: entityToSave.active,
        IsTopSide: entityToSave.isTopSide,
        IsTopSideUS: entityToSave.isTopSideUS,
        PointInTime: entityToSave.PointInTime,
        IsBranch: entityToSave.isBranch,
        ModifiedBy: 'admin',
        EntityShortName: entityToSave.entityShortName,
        Segment: entityToSave.segment,
        reportingBasis: entityToSave.reportingBasis,
        TaxYearEnd: entityToSave.taxYearEnd,
        taxFilingEntityCode: entityToSave.taxFilingEntityCode,
        TaxId: entityToSave.taxId,
        Ownership: entityToSave.ownership,
        CountryOfInc: entityToSave.countryOfInc,
        CountryASU: entityToSave.countryASU,
        CountryCbCR: entityToSave.countryCbCR,
        CountryOECD: entityToSave.countryOECD,
        LegalType: entityToSave.legalType
      };

      if (editMode) {
        // Get the original entity to check if active status changed
        const originalEntity = entities.find((e) => e.id === entityToSave.id);
        const activeStatusChanged = originalEntity && originalEntity.active !== entityToSave.active;

        // Update existing entity
        await api.put(`/entity-models/master/${entityToSave.id}`, backendEntity);

        // Handle EntityInput creation/deletion based on active status change
        if (activeStatusChanged) {
          try {
            if (!entityToSave.active) {
              // Entity set to inactive - delete all EntityInputs for this entity


              const entityInputsResponse = await api.get('/entity-models/input');
              const entityInputs = entityInputsResponse.data || [];
              const entityInputsToDelete = entityInputs.filter((input) => input.EntityCode === entityToSave.code);

              for (const entityInput of entityInputsToDelete) {
                await api.delete(`/entity-models/input/${entityInput.id}`);
              }

              if (entityInputsToDelete.length > 0) {
                setNotification({
                  open: true,
                  message: `Entity set to inactive - ${entityInputsToDelete.length} EntityInput(s) deleted`,
                  severity: 'info'
                });
              }
            } else {
              // Entity set to active - create EntityInputs for all scenarios that have this entity


              const [scenariosResponse, entityInputsResponse] = await Promise.all([
              api.get('/master-scenarios'),
              api.get('/entity-models/input')]
              );

              const scenarios = scenariosResponse.data.data || [];
              const existingEntityInputs = entityInputsResponse.data || [];

              // Find scenarios that have this entity in their entityList
              const scenariosWithEntity = scenarios.filter((scenario) => {
                const entityList = scenario.entityList || [];
                return entityList.includes(entityToSave.code);
              });

              let createdCount = 0;
              for (const scenario of scenariosWithEntity) {
                // Check if EntityInput already exists
                const existingEntityInput = existingEntityInputs.find((input) =>
                input.ScenarioCode === scenario.ScenarioCode &&
                input.EntityCode === entityToSave.code
                );

                if (!existingEntityInput) {
                  // Create new EntityInput with null rates
                  const entityInputData = {
                    ScenarioCode: scenario.ScenarioCode,
                    EntityCode: entityToSave.code,
                    CurrentTaxRate: null,
                    BeginningDeferredTaxRate: null,
                    EndingDeferredTaxRate: null,
                    AverageFXRate: null,
                    BeginningSpotRate: null,
                    EndingSpotRate: null,
                    Year: scenario.Year,
                    Period: scenario.Period,
                    dateCreated: new Date().toISOString(),
                    ModifiedOn: new Date().toISOString(),
                    modifiedBy: 'admin'
                  };

                  await api.post('/entity-models/input', entityInputData);
                  createdCount++;
                }
              }

              if (createdCount > 0) {
                setNotification({
                  open: true,
                  message: `Entity set to active - ${createdCount} EntityInput(s) created for associated scenarios`,
                  severity: 'success'
                });
              }
            }
          } catch (error) {
            console.error('Error managing EntityInputs on active status change:', error);
            setNotification({
              open: true,
              message: 'Entity updated but failed to manage EntityInputs',
              severity: 'warning'
            });
          }
        }

        // Update the entity in state directly, converting PointInTime code to name for display
        const pointInTimeMap = new Map();
        pointInTimes.forEach((pit) => {
          pointInTimeMap.set(pit.Code, pit.Name);
        });

        const updatedEntity = {
          ...entityToSave,
          PointInTime: pointInTimeMap.get(entityToSave.PointInTime) || entityToSave.PointInTime,
          PointInTimeCode: entityToSave.PointInTime // Keep the original code for filtering
        };

        setEntities(entities.map((entity) =>
        entity.id === entityToSave.id ? updatedEntity : entity
        ));

        setNotification({
          open: true,
          message: 'Entity updated successfully',
          severity: 'success'
        });
      } else {
        // Create new entity
        await api.post('/entity-models/master', backendEntity);

        // Refresh the entities list from the API
        await fetchEntities();

        setNotification({
          open: true,
          message: 'Entity created successfully',
          severity: 'success'
        });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving entity:', error);
      setNotification({
        open: true,
        message: 'Failed to save entity',
        severity: 'error'
      });
    }
  };

  // Function to export entities data to Excel
  const exportToExcel = () => {
    try {
      // Determine which entities to export based on selection and current filters
      const entitiesToExport = selectedRows.length > 0 ?
      filteredEntities.filter((entity) => selectedRows.includes(entity.id)) :
      filteredEntities;

      // Create a mapping of field names to display names, excluding the actions column
      const columnMap = {};
      columns.forEach((col) => {
        // Skip action columns, columns without a field property, and columns explicitly marked to disable export
        if (col.field && !col.disableExport && col.field !== 'actions') {
          columnMap[col.field] = col.headerName || col.field;
        }
      });

      let exportData = [];
      let worksheet;

      if (entitiesToExport.length === 0) {
        // Create empty worksheet with just headers
        const headers = Object.values(columnMap);
        worksheet = XLSX.utils.aoa_to_sheet([headers]);
      } else {
        // Map the data using the current columns
        exportData = entitiesToExport.map((entity) => {
          const row = {};
          Object.keys(columnMap).forEach((field) => {
            // Handle special field types
            if (field === 'active') {
              row[columnMap[field]] = entity[field] ? 'Active' : 'Inactive';
            } else if (field === 'isTopSide' || field === 'isTopSideUS' || field === 'isBranch') {
              row[columnMap[field]] = entity[field] ? 'Yes' : 'No';
            } else if (field === 'PointInTime') {
              // Export PointInTime CODE instead of NAME
              row[columnMap[field]] = entity.PointInTimeCode || entity[field];
            } else if (field === 'periodDisplay' && !entity[field] && entity.PointInTime) {
              // Handle period display using PointInTime instead of year and period
              row[columnMap[field]] = entity.PointInTime;
            } else if (field === 'country') {
              // Export country name as-is
              row[columnMap[field]] = entity[field] || '';
            } else {
              // Handle nested properties if needed
              const value = field.split('.').reduce((obj, key) => obj && obj[key] !== undefined ? obj[key] : '', entity);
              row[columnMap[field]] = value !== undefined ? value : '';
            }
          });
          return row;
        });

        // Create a worksheet from the exported data
        worksheet = XLSX.utils.json_to_sheet(exportData);
      }

      // Auto-size columns
      const columnWidths = [];
      const headers = entitiesToExport.length === 0 ?
      Object.values(columnMap) :
      XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];

      // Calculate max width for each column
      headers.forEach((header, index) => {
        let maxLength = String(header).length;
        if (exportData.length > 0) {
          exportData.forEach((row) => {
            const value = row[header];
            if (value !== null && value !== undefined) {
              const length = String(value).length;
              if (length > maxLength) maxLength = length;
            }
          });
        }
        // Add some padding and set reasonable min/max widths
        columnWidths.push({ wch: Math.min(Math.max(maxLength, 10), 50) });
      });

      worksheet['!cols'] = columnWidths;

      // Create a workbook with the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Entities');

      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const fileData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(fileData, `Entities_Export_${new Date().toISOString().split('T')[0]}.xlsx`);

      const message = entitiesToExport.length === 0 ?
      'Empty template exported successfully with column headers' :
      `${entitiesToExport.length} entit${entitiesToExport.length === 1 ? 'y' : 'ies'} exported successfully`;

      setNotification({
        open: true,
        message: message,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting entities:', error);
      setNotification({
        open: true,
        message: 'Failed to export entities: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  // Reference to hidden file input
  const fileInputRef = React.useRef();

  // Handle click on Import button
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Helper function to generate year options for dropdown
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Generate years from 5 years ago to 5 years in the future
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      years.push({ label: i.toString(), value: i });
    }
    return years;
  };

  // Handle file selection for import
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get first worksheet
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Check if file is empty
          if (jsonData.length === 0) {
            throw new Error('The Excel file is empty');
          }

          // Fetch PointInTime master data to map names/codes to codes
          const pointInTimesResponse = await api.get('/point-in-times');
          const pointInTimes = pointInTimesResponse.data.data || [];

          // Create a map that accepts both PointInTime Names and Codes (case-insensitive)
          const pitNameToCodeMap = new Map();
          pointInTimes.forEach((pit) => {
            pitNameToCodeMap.set(pit.Name.toLowerCase().trim(), pit.Code);
            // Map the code to itself so codes work directly (primary format)
            pitNameToCodeMap.set(pit.Code.toLowerCase().trim(), pit.Code);
          });

          // Get all column names from the first row, case-insensitive
          const fileColumns = Object.keys(jsonData[0] || {});
          const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

          // Map frontend column names to possible Excel column names
          const columnMappings = {
            'EntityCode': ['entity code', 'entitycode', 'code'],
            'EntityName': ['entity name', 'entityname', 'name'],
            'EntityShortName': ['entity short name', 'entityshortname', 'short name', 'short'],
            'Region': ['region'],
            'Country': ['country'],
            'Currency': ['currency', 'curr'],
            'LegalType': ['legal type', 'legaltype'],
            'TaxType': ['tax type', 'taxtype'],
            'TaxSubType': ['tax sub type', 'taxsubtype', 'sub type'],
            'Status': ['status', 'active'],
            'PointInTime': ['point in time', 'pointintime', 'pit', 'period'],
            'IsTopSide': ['is top side', 'istopside', 'top side'],
            'IsTopSideUS': ['is top side us', 'istopsideus', 'top side us'],
            'IsBranch': ['is branch', 'isbranch', 'branch'],
            'Segment': ['segment'],
            'ReportingBasis': ['reporting basis', 'reportingbasis'],
            'TaxYearEnd': ['tax year end', 'taxyearend'],
            'TaxFilingEntityCode': ['tax filing entity code', 'taxfilingentitycode'],
            'TaxId': ['tax id', 'taxid'],
            'Ownership': ['ownership'],
            'CountryOfInc': ['country of incorporation', 'countryofinc'],
            'CountryASU': ['country asu', 'countryasu'],
            'CountryCbCR': ['country cbcr', 'countrycbcr'],
            'CountryOECD': ['country oecd', 'countryoecd']
          };

          // Find matching columns in the Excel file
          const matchedColumns = {};
          let missingColumns = [];

          for (const [field, possibleNames] of Object.entries(columnMappings)) {
            const found = fileColumnsLower.some((col, index) => {
              if (possibleNames.includes(col)) {
                matchedColumns[field] = fileColumns[index]; // Store original column name
                return true;
              }
              return false;
            });

            if (!found && ['EntityCode', 'EntityName', 'Region', 'Country', 'Currency', 'TaxType', 'TaxSubType', 'PointInTime'].includes(field)) {
              missingColumns.push(possibleNames[0]); // Use the first name as the display name
            }
          }

          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }

          // First, fetch all existing entities to check for duplicates
          const existingEntities = await api.get('/entity-models/master');
          // Map by both EntityCode and PointInTime for composite key matching
          const entitiesMap = new Map(
            (existingEntities.data.data || []).map((ent) => [
            `${ent.EntityCode.toLowerCase()}_${ent.PointInTime.toLowerCase()}`,
            ent]
            )
          );

          // Process each row and prepare for import/update
          const importResults = {
            created: 0,
            updated: 0,
            errors: []
          };

          const processRow = async (row, index) => {
            try {
              // Extract values using matched column names
              const entityCode = String(row[matchedColumns.EntityCode] || '').trim();
              const entityName = String(row[matchedColumns.EntityName] || '').trim();
              const region = String(row[matchedColumns.Region] || '').trim();
              const country = String(row[matchedColumns.Country] || '').trim();
              const currency = String(row[matchedColumns.Currency] || '').trim();
              const taxType = String(row[matchedColumns.TaxType] || '').trim();
              const taxSubType = String(row[matchedColumns.TaxSubType] || '').trim();
              const pointInTimeInput = String(row[matchedColumns.PointInTime] || '').trim();
              const status = String(row[matchedColumns.Status] || '').trim();

              // Validate required fields
              if (!entityCode) {
                throw new Error('Entity Code is required');
              }
              if (!entityName) {
                throw new Error('Entity Name is required');
              }
              if (!region) {
                throw new Error('Region is required');
              }
              if (!country) {
                throw new Error('Country is required');
              }
              if (!currency) {
                throw new Error('Currency is required');
              }
              // TaxType and TaxSubType are optional fields
              if (!pointInTimeInput) {
                throw new Error('Point In Time is required');
              }

              // Validate Country against dropdown options (jurisdictions) - case-insensitive
              const matchedCountry = jurisdictions.find((j) => j.toLowerCase() === country.toLowerCase());
              if (!matchedCountry) {
                throw new Error(`Invalid Country: "${country}". Must be one of: ${jurisdictions.join(', ')}`);
              }
              const validatedCountry = matchedCountry;

              // Validate LegalType against dropdown options (if provided) - case-insensitive
              const legalTypeValue = String(row[matchedColumns.LegalType] || '').trim();
              let validatedLegalType = legalTypeValue;
              if (legalTypeValue) {
                const matchedLegalType = legalTypes.find((lt) => lt.toLowerCase() === legalTypeValue.toLowerCase());
                if (!matchedLegalType) {
                  throw new Error(`Invalid Legal Type: "${legalTypeValue}". Must be one of: ${legalTypes.join(', ')}`);
                }
                validatedLegalType = matchedLegalType;
              }

              // Validate TaxType against dropdown options (if provided) - case-insensitive
              let validatedTaxType = taxType;
              if (taxType) {
                const matchedTaxType = taxTypes.find((tt) => tt.toLowerCase() === taxType.toLowerCase());
                if (!matchedTaxType) {
                  throw new Error(`Invalid Tax Type: "${taxType}". Must be one of: ${taxTypes.join(', ')}`);
                }
                validatedTaxType = matchedTaxType;
              }

              // Validate TaxSubType against dropdown options (if provided) - case-insensitive
              let validatedTaxSubType = taxSubType;
              if (taxSubType) {
                const matchedTaxSubType = taxSubTypes.find((tst) => tst.toLowerCase() === taxSubType.toLowerCase());
                if (!matchedTaxSubType) {
                  throw new Error(`Invalid Tax Sub Type: "${taxSubType}". Must be one of: ${taxSubTypes.join(', ')}`);
                }
                validatedTaxSubType = matchedTaxSubType;
              }

              // Map PointInTime Name to Code (case-insensitive lookup)
              const pointInTimeCode = pitNameToCodeMap.get(pointInTimeInput.toLowerCase().trim());

              if (!pointInTimeCode) {
                throw new Error(`Invalid Point In Time: "${pointInTimeInput}". Must match a valid PointInTime Name or Code.`);
              }

              // Build payload with PointInTime CODE and validated dropdown values
              const entityData = {
                EntityCode: entityCode,
                EntityName: entityName,
                EntityShortName: String(row[matchedColumns.EntityShortName] || '').trim(),
                Region: region,
                Country: validatedCountry,
                Currency: currency,
                LegalType: validatedLegalType,
                TaxType: validatedTaxType,
                TaxSubType: validatedTaxSubType,
                Active: status.toLowerCase() === 'active' ? true : false,
                IsTopSide: String(row[matchedColumns.IsTopSide] || '').toLowerCase() === 'yes' ? true : false,
                IsTopSideUS: String(row[matchedColumns.IsTopSideUS] || '').toLowerCase() === 'yes' ? true : false,
                IsBranch: String(row[matchedColumns.IsBranch] || '').toLowerCase() === 'yes' ? true : false,
                PointInTime: pointInTimeCode, // Store the CODE, not the name
                ModifiedBy: 'admin',
                Segment: String(row[matchedColumns.Segment] || '').trim(),
                reportingBasis: String(row[matchedColumns.ReportingBasis] || '').trim(),
                TaxYearEnd: String(row[matchedColumns.TaxYearEnd] || '').trim(),
                taxFilingEntityCode: String(row[matchedColumns.TaxFilingEntityCode] || '').trim(),
                TaxId: String(row[matchedColumns.TaxId] || '').trim(),
                Ownership: String(row[matchedColumns.Ownership] || '').trim(),
                CountryOfInc: String(row[matchedColumns.CountryOfInc] || '').trim(),
                CountryASU: String(row[matchedColumns.CountryASU] || '').trim(),
                CountryCbCR: String(row[matchedColumns.CountryCbCR] || '').trim(),
                CountryOECD: String(row[matchedColumns.CountryOECD] || '').trim()
              };

              // Check if entity with this code AND PointInTime already exists
              const compositeKey = `${entityCode.toLowerCase()}_${pointInTimeCode.toLowerCase()}`;
              const existingEntity = entitiesMap.get(compositeKey);

              if (existingEntity) {
                // Update existing entity (same EntityCode and PointInTime)
                await api.put(`/entity-models/master/${existingEntity.id}`, entityData);
                importResults.updated++;
              } else {
                // Create new entity (different EntityCode or PointInTime combination)
                await api.post('/entity-models/master', entityData);
                importResults.created++;
              }

              return { success: true };

            } catch (error) {
              console.error(`Error processing row ${index + 1}:`, row, error);
              const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
              importResults.errors.push(`Row ${index + 1}: ${errorMessage}`);
              return { success: false, error: errorMessage };
            }
          };

          // Process rows sequentially to avoid race conditions
          for (let i = 0; i < jsonData.length; i++) {
            await processRow(jsonData[i], i);
          }

          // Check if there were any errors
          if (importResults.errors.length > 0) {
            const errorMessage = importResults.errors.length === jsonData.length ?
            'All rows had errors. No data was imported.' :
            `Completed with ${importResults.errors.length} error(s) out of ${jsonData.length} rows.`;

            if (importResults.created === 0 && importResults.updated === 0) {
              throw new Error(`Import failed: ${errorMessage}\n\n${importResults.errors.join('\n')}`);
            } else {
              // Some rows succeeded, show warning
              setNotification({
                open: true,
                message: `Partial import: ${importResults.created} created, ${importResults.updated} updated, ${importResults.errors.length} errors`,
                severity: 'warning',
                autoHideDuration: 10000
              });
            }
          }

          // Refresh data
          await fetchEntities();

          // Only show success message if no errors were shown before
          if (importResults.errors.length === 0) {
            const updatedMsg = importResults.updated > 0 ? `, updated ${importResults.updated}` : '';
            setNotification({
              open: true,
              message: `Successfully imported ${importResults.created} entit${importResults.created !== 1 ? 'ies' : 'y'}${updatedMsg}`,
              severity: 'success',
              autoHideDuration: 5000
            });
          }
        } catch (error) {
          console.error('Error processing import:', error);
          setNotification({
            open: true,
            message: `Import failed: ${error.message || 'Unknown error'}`,
            severity: 'error'
          });
        }
      };

      reader.onerror = () => {
        setNotification({
          open: true,
          message: 'Error reading file',
          severity: 'error'
        });
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error in import handler:', error);
      setNotification({
        open: true,
        message: `Import failed: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setImporting(false);
      // Clear the file input
      event.target.value = null;
    }
  };

  // Handle input change for entity form
  const handleInputChange = (e) => {
    setCurrentEntity({
      ...currentEntity,
      [e.target.name]: e.target.value
    });
  };

  // Handle viewing users for an entity
  const handleViewUsers = (entity) => {
    setSelectedEntityForUsers(entity);
    setUserDialogOpen(true);
    fetchEntityUsers(entity.id);
  };

  // Handle assigning a user to an entity
  const handleAssignUser = (entity) => {
    setSelectedEntityForUsers(entity);
    setAssignUserDialogOpen(true);
  };

  // Handle closing the user dialog
  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
  };

  // Fetch users for an entity
  const fetchEntityUsers = async (entityId) => {
    setLoadingUsers(true);
    try {
      // Uncomment when API is ready
      // const response = await api.get(`/entities/${entityId}/users`);
      // setEntityUsers(response.data.data || []);

      // Mock data for entity users
      const mockUsers = [
      { id: 1, firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com', role: 'admin' },
      { id: 2, firstName: 'Jane', lastName: 'Smith', email: 'jane.smith@example.com', role: 'tax_manager' }];

      setEntityUsers(mockUsers);
    } catch (error) {
      console.error('Error fetching entity users:', error);
      setNotification({
        open: true,
        message: 'Failed to load entity users',
        severity: 'error'
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle user assignment completion
  const handleUserAssigned = () => {
    setAssignUserDialogOpen(false);
    setNotification({
      open: true,
      message: 'User assigned successfully',
      severity: 'success'
    });
    if (selectedEntityForUsers) {
      fetchEntityUsers(selectedEntityForUsers.id);
    }
  };

  // Handle removing a user from an entity
  const handleRemoveUser = async (userId) => {
    if (window.confirm('Are you sure you want to remove this user from the entity?')) {
      try {
        // Uncomment when API is ready
        // await api.delete(`/users/${userId}/assign`, {
        //   data: { entityId: selectedEntityForUsers.id }
        // });
        setEntityUsers(entityUsers.filter((user) => user.id !== userId));
        setNotification({
          open: true,
          message: 'User removed successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error removing user:', error);
        setNotification({
          open: true,
          message: 'Failed to remove user',
          severity: 'error'
        });
      }
    }
  };

  // Enhanced columns with more metadata fields
  const columns = [
  { field: 'code', headerName: 'CODE', width: 70,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'var(--color-black)' }}>
          {params.value}
        </Typography>

  },
  { field: 'name', headerName: 'NAME', width: 120,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%', lineHeight: '1.5', paddingY: '4px' }}>
          {params.value}
        </Typography>

  },
  { field: 'entityShortName', headerName: 'SHORT', width: 75 },
  { field: 'region', headerName: 'REGION', width: 75 },
  { field: 'country', headerName: 'COUNTRY', width: 130 },
  { field: 'currency', headerName: 'CURR', width: 70 },
  { field: 'legalType', headerName: 'LEGAL TYPE', width: 100 },
  { field: 'taxType', headerName: 'TAX TYPE', width: 100 },
  { field: 'taxSubType', headerName: 'SUB TYPE', width: 85 },
  { field: 'PointInTime', headerName: 'POINT IN TIME', width: 150 },
  { field: 'active', headerName: 'STATUS', width: 85, type: 'boolean',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value ? 'Active' : 'Inactive'}
        </Typography>

  },
  { field: 'taxId', headerName: 'TAX ID', width: 100 },
  { field: 'taxFilingEntityCode', headerName: 'TAX FILING CODE', width: 120 },
  { field: 'segment', headerName: 'SEGMENT', width: 100 },
  { field: 'reportingBasis', headerName: 'REPORTING BASIS', width: 120 },
  { field: 'taxYearEnd', headerName: 'TAX YEAR END', width: 120 },
  { field: 'countryOfInc', headerName: 'INC COUNTRY', width: 120 },
  { field: 'ownership', headerName: 'OWNERSHIP', width: 120 },
  { field: 'countryASU', headerName: 'ASU COUNTRY', width: 120 },
  { field: 'countryCbCR', headerName: 'CbCR COUNTRY', width: 120 },
  { field: 'countryOECD', headerName: 'OECD COUNTRY', width: 120 },
  { field: 'isTopSide', headerName: 'TOP SIDE', width: 85, type: 'boolean',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value ? 'Yes' : 'No'}
        </Typography>

  },
  { field: 'isTopSideUS', headerName: 'TOP SIDE US', width: 100, type: 'boolean',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value ? 'Yes' : 'No'}
        </Typography>

  },
  { field: 'isBranch', headerName: 'BRANCH', width: 85, type: 'boolean',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) =>
    <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
          {params.value ? 'Yes' : 'No'}
        </Typography>

  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 90,
    renderCell: (params) =>
    <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} title="Edit Entity">
            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row.id)} title="Delete Entity">
            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

  }];


  // User columns for the DataGrid
  const userColumns = [
  { field: 'firstName', headerName: 'First Name', width: 130 },
  { field: 'lastName', headerName: 'Last Name', width: 130 },
  { field: 'email', headerName: 'Email', width: 200 },
  { field: 'role', headerName: 'Role', width: 130 },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    renderCell: (params) =>
    <IconButton size="small" onClick={() => handleRemoveUser(params.row.id)} title="Remove User">
          <img src="/assets/Icons/trashIcon.svg" alt="Delete" style={{ width: 18, height: 18 }} />
        </IconButton>

  }];


  // Filter entities based on the selected PointInTime
  const filteredEntities = React.useMemo(() => {
    if (selectedPointInTime === 'All') {
      return entities;
    }

    // Filter entities where PointInTimeCode matches the selected PointInTime
    return entities.filter((entity) => entity.PointInTimeCode === selectedPointInTime);
  }, [entities, selectedPointInTime]);

  return (
    <Box className="entity-management-container">
        <EntityTable
        entities={filteredEntities}
        columns={columns}
        loading={loading}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        onAddNew={handleAdd}
        onDeleteSelected={handleDeleteSelected}
        onExport={exportToExcel}
        onImport={handleImportClick}
        onRefresh={fetchEntities}
        fileInputRef={fileInputRef}
        handleFileChange={handleFileChange}
        importing={importing} />


      {/* Entity Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)} />

      <EntityDialog
        open={openDialog}
        onClose={handleRequestClose}
        onCancel={handleClose}
        onSave={handleSave}
        entity={currentEntity}
        editMode={editMode}
        pointInTimes={pointInTimes} />


      {/* User Dialog */}
      <Dialog open={userDialogOpen} onClose={handleCloseUserDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedEntityForUsers && `Users for ${selectedEntityForUsers.name}`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <SearchableDataTable
              searchPlaceholder="Search Users..."
              rows={entityUsers}
              columns={userColumns}
              loading={loadingUsers}
              pageSize={5}
              tableName="entity-users-table"
              defaultSortField="lastName"
              defaultSortOrder="asc" />

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      {selectedEntityForUsers &&
      <AssignUserDialog
        open={assignUserDialogOpen}
        onClose={() => setAssignUserDialogOpen(false)}
        onUserAssigned={handleUserAssigned}
        entityId={selectedEntityForUsers.id}
        entityName={selectedEntityForUsers.name} />

      }


      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}>

        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}>

          {notification.message}
        </Alert>
      </Snackbar>
    </Box>);

};

export default EntityManagement;
